import {
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_TYPE,
} from '../constants/constants';
import {Permission} from '../types/permission-group/permission';
import {PermissionGroup} from '../types/permission-group/permissionGroup';
import {ItemType} from '../components/pages/user-management/tabs/AddClientInfo';

export type PermissionGroupsSliceState = {
  permissionGroups: PermissionGroup[];
  newlyAddedPermissionGroupId: string;
  newlyAddedPermissionGroupClientId: string;
  newlyAddedPermissionGroupCategory: PERMISSION_GROUP_CATEGORY | '';
};

export const isNewlyAddedPermissionGroupForClient = (
  permissionGroupsState: PermissionGroupsSliceState,
  clientId: string | undefined
): boolean => {
  if (
    !permissionGroupsState.newlyAddedPermissionGroupId ||
    !permissionGroupsState.newlyAddedPermissionGroupCategory ||
    !clientId ||
    !permissionGroupsState.newlyAddedPermissionGroupClientId
  ) {
    return false;
  }
  return permissionGroupsState.newlyAddedPermissionGroupClientId === clientId;
};

export const mergePermissionGroupsFromRedux = (
  apiGroups: PermissionGroup[],
  permissionGroupsState: PermissionGroupsSliceState,
  clientId: string | undefined,
  category: PERMISSION_GROUP_CATEGORY
): PermissionGroup[] => {
  if (!isNewlyAddedPermissionGroupForClient(permissionGroupsState, clientId)) {
    return apiGroups;
  }

  const {
    newlyAddedPermissionGroupId,
    newlyAddedPermissionGroupCategory,
    permissionGroups,
  } = permissionGroupsState;

  if (newlyAddedPermissionGroupCategory !== category) {
    return apiGroups;
  }

  const fromRedux = permissionGroups.find(
    (group) =>
      group.permissionGroupId === newlyAddedPermissionGroupId &&
      group.clientId === clientId &&
      group.category === category
  );
  if (
    !fromRedux ||
    apiGroups.some(
      (group) => group.permissionGroupId === newlyAddedPermissionGroupId
    )
  ) {
    return apiGroups;
  }

  return [...apiGroups, fromRedux].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
};

/** Merges API read-model with redux write-model groups not yet visible on GET (CQRS lag). */
export const mergePermissionGroupsWithWriteModel = (
  apiGroups: PermissionGroup[],
  permissionGroupsState: PermissionGroupsSliceState,
  clientId: string | undefined,
  category: PERMISSION_GROUP_CATEGORY
): PermissionGroup[] => {
  const mergedFromNewlyAdded = mergePermissionGroupsFromRedux(
    apiGroups,
    permissionGroupsState,
    clientId,
    category
  );

  if (!clientId) {
    return mergedFromNewlyAdded;
  }

  const pendingFromStore = permissionGroupsState.permissionGroups.filter(
    (group) =>
      group.clientId === clientId &&
      group.category === category &&
      !mergedFromNewlyAdded.some(
        (existing) => existing.permissionGroupId === group.permissionGroupId
      )
  );

  if (pendingFromStore.length === 0) {
    return mergedFromNewlyAdded;
  }

  return [...mergedFromNewlyAdded, ...pendingFromStore].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
};

/** Preserves locally upserted groups when a stale GET overwrites option lists. */
export const mergePermissionGroupOptionLists = (
  fromApi: ItemType[],
  existing: ItemType[]
): ItemType[] => {
  let merged = fromApi;
  for (const item of existing) {
    merged = upsertPermissionGroupItem(merged, item);
  }
  return merged;
};

export const upsertPermissionGroupItem = (
  options: ItemType[],
  item: ItemType
): ItemType[] => {
  if (options.some((option) => option.ItemKey === item.ItemKey)) {
    return options;
  }
  return [...options, item].sort((a, b) =>
    a.ItemName.toLowerCase().localeCompare(b.ItemName.toLowerCase())
  );
};

export const separatePermissionsByType = (
  permissions: Permission[],
  type: string
) =>
  permissions
    .filter((perm: Permission) => perm.type === type)
    .map((perm: Permission) => perm.permissionCode);

export const permissionGroupToItemType = (
  group: PermissionGroup
): ItemType => ({
  ItemKey: group.permissionGroupId,
  ItemName: group.name,
  ItemDescription: group.description || '',
  ItemFeatures: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.REPORTS
  ),
  ItemFeaturesForECI: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.ECI
  ),
  ItemFeaturesForBudget: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.BUDGET
  ),
  ItemFeaturesForSystem: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.SYSTEM
  ),
  ItemFeaturesForManageProject: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.MANAGE_PROJECT
  ),
  ItemEvidenceSourceManagement: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.EVIDENCE
  ),
  ItemFeaturesForBusinessIntelligence: separatePermissionsByType(
    group.permissions,
    PERMISSION_TYPE.BUSINESS_INTELLIGENCE
  ),
  permissions: group.permissions,
});

export const resolvePermissionGroupItemType = (
  permissionGroupId: string | undefined,
  optionList?: ItemType[],
  permissionGroups?: PermissionGroup[]
): ItemType | undefined => {
  if (!permissionGroupId) {
    return undefined;
  }

  const fromList = optionList?.find(
    (item) => item.ItemKey === permissionGroupId
  );
  if (fromList) {
    return fromList;
  }

  const fromGroups = permissionGroups?.find(
    (group) => group.permissionGroupId === permissionGroupId
  );
  return fromGroups ? permissionGroupToItemType(fromGroups) : undefined;
};
